/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import HtmlFragment from '@/components/html-fragment';
import { Tabs } from '@/components/tabs';
import { getSizeChartAttributeId } from '@/lib/product/product-utils';
import { UITarget } from '@/targets/ui-target';

interface ProductTabsProps {
    product: ShopperProducts.schemas['Product'];
    className?: string;
}

/**
 * Horizontal tab block below the PDP's split-column fold, replacing the former
 * "Description" / "Product Details" accordion pair.
 *
 * - **Product Details** merges the catalog description with the adapter-backed material / fit /
 *   care copy injected at `sfcc.pdp.details.content` (Product Content extension).
 * - **Size Chart** renders the dimensional sizing breakdown injected at
 *   `sfcc.pdp.sizeChart.content`.
 *
 * Both bodies come through UITargets rather than direct imports so the tabs keep working with the
 * Product Content extension uninstalled — the targets then render nothing and the panels fall back
 * to the catalog copy alone.
 *
 * The tab items are built inline in the returned JSX on purpose: the build-time UITarget transform
 * only rewrites `<UITarget>` elements reachable from a return statement's JSX tree, so hoisting
 * these into a `useMemo` would strip the import while leaving the elements behind.
 */
export default function ProductTabs({ product, className }: ProductTabsProps): ReactElement {
    const { t } = useTranslation('product');

    // Only the long-form copy belongs here: `shortDescription` already renders under the price in
    // ProductInfo, so falling back to it (or to a `longDescription` that merely repeats it) would
    // print the same paragraph twice on the page.
    const description =
        product.longDescription && product.longDescription !== product.shortDescription
            ? product.longDescription
            : undefined;

    // Same rule as the inline Size Chart button in ProductInfo: no size-like attribute, no Size Chart tab.
    const hasSizeChart = Boolean(getSizeChartAttributeId(product.variationAttributes));

    return (
        <Tabs
            label={t('productInformation')}
            className={className}
            variant="card"
            items={[
                {
                    id: 'product-details',
                    label: t('productDetails'),
                    content: (
                        <div className="space-y-6">
                            {description ? <HtmlFragment content={description} contentType="plain-text" /> : null}
                            <UITarget targetId="sfcc.pdp.details.content" />
                        </div>
                    ),
                },
                ...(hasSizeChart
                    ? [
                          {
                              id: 'size-chart',
                              label: t('sizeChart'),
                              content: <UITarget targetId="sfcc.pdp.sizeChart.content" />,
                          },
                      ]
                    : []),
            ]}
        />
    );
}
